/**
 * Notification Preferences Component
 * Allows users to configure their notification settings
 */

import React, { useState, useEffect } from 'react';
import { Bell, Mail, MessageSquare, AlertTriangle, CheckCircle } from 'lucide-react';
import { getAPIBaseURL } from '../utils/apiConfig';

const NotificationPreferences = ({ token, onClose }) => {
  const API_BASE = getAPIBaseURL();
  const [preferences, setPreferences] = useState({
    email_new_leads: true,
    email_status_updates: true,
    email_marketing: false,
    push_new_leads: true,
    push_status_updates: true,
    sms_urgent_only: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Load current preferences
  useEffect(() => {
    const loadPreferences = async () => {
      if (!token) return;

      try {
        const response = await fetch(`${API_BASE}/notifications/preferences`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setPreferences(data.preferences);
        } else {
          setError('Failed to load notification preferences');
        }
      } catch (error) {

        setError('Failed to load notification preferences');
      } finally {
        setLoading(false);
      }
    };

    loadPreferences();
  }, [token]);

  // Handle preference change
  const handlePreferenceChange = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
    setSuccess(false);
    setError(null);
  };

  // Save preferences
  const savePreferences = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await fetch(`${API_BASE}/notifications/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(preferences)
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save preferences');
      }
    } catch (error) {

      setError('Failed to save notification preferences');
    } finally {
      setSaving(false);
    }
  };

  const PreferenceToggle = ({
    id,
    label,
    description,
    checked,
    onChange,
    icon: Icon,
    disabled = false
  }) => (
    <div className="flex items-start space-x-3 p-4 border rounded-lg">
      <div className="flex-shrink-0">
        <Icon className={`w-5 h-5 mt-0.5 ${checked ? 'text-orange-500' : 'text-gray-400'}`} />
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <div>
            <label htmlFor={id} className="text-sm font-medium text-gray-900 cursor-pointer">
              {label}
            </label>
            <p className="text-sm text-gray-500 mt-1">{description}</p>
          </div>
          <div className="flex-shrink-0 ml-4">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                id={id}
                checked={checked}
                onChange={(e) => onChange(id, e.target.checked)}
                disabled={disabled}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 flex items-center">
            <Bell className="w-6 h-6 mr-2 text-orange-500" />
            Notification Preferences
          </h2>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <p className="text-gray-600 mt-2">
          Control how and when you receive notifications about your service requests and account activity.
        </p>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg flex items-center">
          <AlertTriangle className="w-5 h-5 text-orange-500 mr-2" />
          <span className="text-orange-700">{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-300 rounded-lg flex items-center">
          <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
          <span className="text-green-700 font-medium">Notification preferences updated successfully!</span>
        </div>
      )}

      {/* Email Notifications */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Mail className="w-5 h-5 mr-2 text-gray-600" />
          Email Notifications
        </h3>
        <div className="space-y-3">
          <PreferenceToggle
            id="email_new_leads"
            label="New Service Requests"
            description="Get notified when customers request your services"
            icon={Bell}
            checked={preferences.email_new_leads}
            onChange={handlePreferenceChange}
          />
          <PreferenceToggle
            id="email_status_updates"
            label="Status Updates"
            description="Receive updates when service request status changes"
            icon={MessageSquare}
            checked={preferences.email_status_updates}
            onChange={handlePreferenceChange}
          />
          <PreferenceToggle
            id="email_marketing"
            label="Marketing & Promotions"
            description="Get tips, platform updates, and promotional offers"
            icon={Mail}
            checked={preferences.email_marketing}
            onChange={handlePreferenceChange}
          />
        </div>
      </div>

      {/* Push Notifications */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Bell className="w-5 h-5 mr-2 text-gray-600" />
          Push Notifications
        </h3>
        <div className="space-y-3">
          <PreferenceToggle
            id="push_new_leads"
            label="New Service Requests"
            description="Real-time notifications for new customer requests"
            icon={Bell}
            checked={preferences.push_new_leads}
            onChange={handlePreferenceChange}
          />
          <PreferenceToggle
            id="push_status_updates"
            label="Status Updates"
            description="Instant notifications for service request updates"
            icon={MessageSquare}
            checked={preferences.push_status_updates}
            onChange={handlePreferenceChange}
          />
        </div>
      </div>

      {/* SMS Notifications - TCPA Compliant */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <MessageSquare className="w-5 h-5 mr-2 text-gray-600" />
          SMS Notifications (Text Messages)
        </h3>

        {/* TCPA Compliance Notice */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <MessageSquare className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <strong>SMS Terms:</strong> By enabling SMS notifications, you consent to receive text messages about
                restoration leads and platform updates. Message frequency varies. Message and data rates may apply.
                Reply STOP to opt out or HELP for assistance.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <PreferenceToggle
            id="sms_urgent_only"
            label="Emergency & Urgent Requests Only"
            description="SMS notifications for emergency and urgent service requests only"
            icon={AlertTriangle}
            checked={preferences.sms_urgent_only}
            onChange={handlePreferenceChange}
          />

          {/* Add full SMS consent option if we want to expand */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <div className="flex items-start space-x-3">
              <MessageSquare className="w-5 h-5 mt-0.5 text-gray-600" />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 mb-2">
                  SMS Consent Management
                </p>
                <p className="text-sm text-gray-600 mb-3">
                  You can always opt out of SMS messages by replying STOP to any text. This will not affect
                  email or push notifications. To re-enable SMS, you'll need to update these preferences.
                </p>
                <div className="text-xs text-gray-500">
                  <strong>Customer Support:</strong> Reply HELP to any text or contact support@flood-and-fire-pros.direct
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex items-center justify-between pt-6 border-t">
        <p className="text-sm text-gray-500">
          Changes will take effect immediately after saving.
        </p>
        <button
          onClick={savePreferences}
          disabled={saving}
          className="px-6 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4 mr-2" />
              Save Preferences
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default NotificationPreferences;